import type { ScoreBreakdown, ScoreInput } from './types';

// -----------------------------------------------------------------------------
// Weight budget: 100 points total
//   AUM           → 30 pts  (logarithmic scale)
//   ROA           → 20 pts  (linear, capped at 2%)
//   Origin        → 10 pts  (bonus for indicacao_interna)
//   Tenure        → 15 pts  (sweet-spot 3–8 years)
//   Qualitative   → 25 pts  (average of 3 dimensions, scaled)
// -----------------------------------------------------------------------------

const AUM_MAX_POINTS = 30;
const ROA_MAX_POINTS = 20;
const ORIGIN_MAX_POINTS = 10;
const TENURE_MAX_POINTS = 15;
const QUALITATIVE_MAX_POINTS = 25;

/**
 * AUM score on a log10 scale.
 *
 * Reference thresholds (R$):
 *   ≥ R$ 500 M  → 30 pts  (log10 ≈ 10.70)
 *   R$ 50 M     → ~20 pts (log10 ≈ 9.70)
 *   R$ 5 M      → ~10 pts (log10 ≈ 8.70)
 *   < R$ 100 K  →  0 pts
 */
function calcAumPoints(aum_cents: number): number {
  if (aum_cents <= 0) return 0;

  // Convert centavos → reais for readability
  const aum_brl = aum_cents / 100;

  if (aum_brl < 100_000) return 0;

  const log = Math.log10(aum_brl);

  // log10(100_000) ≈ 5 → 0 pts; log10(500_000_000) ≈ 8.7 → 30 pts
  const MIN_LOG = Math.log10(100_000);   // 5
  const MAX_LOG = Math.log10(500_000_000); // ~8.699

  const ratio = (log - MIN_LOG) / (MAX_LOG - MIN_LOG);
  return Math.round(Math.min(1, Math.max(0, ratio)) * AUM_MAX_POINTS);
}

/**
 * ROA score — linear from 0 to 2% (200 bps = max).
 * Above 2% still caps at max (outliers don't get extra credit).
 */
function calcRoaPoints(roa_bps: number): number {
  if (roa_bps <= 0) return 0;
  const ROA_CAP_BPS = 200; // 2.00%
  const ratio = Math.min(roa_bps / ROA_CAP_BPS, 1);
  return Math.round(ratio * ROA_MAX_POINTS);
}

/**
 * Origin bonus — indicação interna is the highest-quality signal.
 */
function calcOriginBonus(origin: ScoreInput['origin']): number {
  const map: Record<ScoreInput['origin'], number> = {
    indicacao_interna: 10,
    ativo:             8,
    headhunter:        6,
    evento:            4,
    linkedin:          2,
  };
  return map[origin] ?? 0;
}

/**
 * Tenure score — penalises both very short (<1 yr) and very long (>12 yrs).
 * Peak points awarded in the 3–8 year range.
 *
 * Piecewise linear:
 *   0 – 1  yr  →  0 –  5 pts
 *   1 – 3  yrs →  5 – 10 pts
 *   3 – 8  yrs → 10 – 15 pts  (peak)
 *   8 – 12 yrs → 15 –  8 pts  (decay)
 *   > 12   yrs →  8 pts        (floor)
 */
function calcTenurePoints(years: number): number {
  if (years < 0) return 0;
  if (years < 1)  return Math.round((years / 1) * 5);
  if (years < 3)  return Math.round(5 + ((years - 1) / 2) * 5);
  if (years <= 8) return Math.round(10 + ((years - 3) / 5) * 5);
  if (years <= 12) return Math.round(15 - ((years - 8) / 4) * 7);
  return 8;
}

/**
 * Qualitative score — average of three dimensions (1–5), scaled to 0–25.
 * If no qualitative data exists, returns the neutral mid-point (15 pts).
 */
function calcQualitativePoints(
  q: ScoreInput['qualitative'],
): number {
  if (!q) {
    // Neutral default: (3/5) * 25 = 15
    return 15;
  }
  const avg = (q.entrepreneurship + q.execution + q.ambition) / 3;
  // Scale [1–5] → [0–1]
  const ratio = (avg - 1) / 4;
  return Math.round(ratio * QUALITATIVE_MAX_POINTS);
}

// -----------------------------------------------------------------------------
// Public API
// -----------------------------------------------------------------------------

/**
 * Returns a detailed breakdown of each component and the final 0–100 score.
 * The `total` is the sum of all components, already clamped to [0, 100].
 */
export function calcScoreBreakdown(input: ScoreInput): ScoreBreakdown {
  const aum_points        = calcAumPoints(input.aum_cents);
  const roa_points        = calcRoaPoints(input.roa_bps);
  const origin_bonus      = calcOriginBonus(input.origin);
  const tenure_points     = calcTenurePoints(input.tenure_years);
  const qualitative_points = calcQualitativePoints(input.qualitative);

  const total = Math.min(
    100,
    aum_points + roa_points + origin_bonus + tenure_points + qualitative_points,
  );

  return {
    aum_points,
    roa_points,
    origin_bonus,
    tenure_points,
    qualitative_points,
    total,
  };
}

/**
 * Convenience wrapper — returns just the final integer score (0–100).
 * Use this value to persist in `candidates.score`.
 */
export function calcScore(input: ScoreInput): number {
  return calcScoreBreakdown(input).total;
}
